import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { CalenderIcon, DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";

import { useAddPriceQuoteModal, PRICE_QUOTE_LABELS } from "../../hooks/useAddPriceQuoteModal";
import { selectCustomerPriceQuoteStatusLabelKey } from "../../../../redux/selector";
import {
  CustomerPriceQuoteProjectType,
  CustomerPriceQuoteStatus,
} from "../../../../types";
import { useStyles } from "./styles";

const CURRENCY_SYMBOL = "$";
const PROJECT_TYPES = Object.values(CustomerPriceQuoteProjectType);
const STATUSES = Object.values(CustomerPriceQuoteStatus);

type ModalState = ReturnType<typeof useAddPriceQuoteModal>;

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  const styles = useStyles();

  return (
    <Box className={styles.field}>
      <Typography className={styles.label}>{label}</Typography>
      {children}
    </Box>
  );
};

const statusClassName = (
  styles: ReturnType<typeof useStyles>,
  status: CustomerPriceQuoteStatus
) => {
  if (status === CustomerPriceQuoteStatus.SENT) {
    return `${styles.statusChip} ${styles.sentChip}`;
  }
  if (status === CustomerPriceQuoteStatus.ACCEPTED) {
    return `${styles.statusChip} ${styles.acceptedChip}`;
  }
  return `${styles.statusChip} ${styles.draftChip}`;
};

const PriceQuoteFields = ({ modal }: { modal: ModalState }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const label = (key: string) => t(`${PRICE_QUOTE_LABELS}.TABLE.${key}`);

  return (
    <>
      <Field label={label("PROJECT_TYPE")}>
        <TextField
          select
          className={styles.input}
          value={modal.form.projectType}
          onChange={(event) => modal.setProjectType(event.target.value)}
          SelectProps={{
            displayEmpty: true,
            IconComponent: DropdownIcon,
            renderValue: (selected) =>
              selected ? (
                t(String(selected))
              ) : (
                <span className={styles.placeholder}>{label("PROJECT_TYPE")}</span>
              ),
          }}
        >
          {PROJECT_TYPES.map((projectType) => (
            <MenuItem key={projectType} value={projectType}>
              {t(projectType)}
            </MenuItem>
          ))}
        </TextField>
      </Field>

      <Field label={label("REVISION_NUMBER")}>
        <TextField
          className={styles.input}
          value={modal.form.revisionNumber}
          placeholder={label("REVISION_NUMBER")}
          inputProps={{ inputMode: "numeric" }}
          onChange={(event) =>
            modal.setNumericField("revisionNumber", event.target.value)
          }
        />
      </Field>

      <Field label={label("TOTAL_VALUE")}>
        <Box className={styles.valueField}>
          <TextField
            className={styles.input}
            value={modal.form.totalValue}
            placeholder={label("TOTAL_VALUE")}
            inputProps={{ inputMode: "numeric" }}
            onChange={(event) =>
              modal.setNumericField("totalValue", event.target.value)
            }
          />
          <span className={styles.currency} style={{ left: 16, right: "auto" }}>
            {CURRENCY_SYMBOL}
          </span>
        </Box>
      </Field>

      <Box className={styles.splitRow}>
        <Field label={label("STATUS")}>
          <TextField
            select
            className={styles.input}
            value={modal.form.status}
            onChange={(event) => {
              const nextStatus = STATUSES.find(
                (status) => status === event.target.value
              );
              if (nextStatus) modal.setStatus(nextStatus);
            }}
            SelectProps={{
              IconComponent: DropdownIcon,
              renderValue: (selected) => {
                const status =
                  STATUSES.find((item) => item === selected) ||
                  CustomerPriceQuoteStatus.DRAFT;
                return (
                  <span className={statusClassName(styles, status)}>
                    {t(selectCustomerPriceQuoteStatusLabelKey(status))}
                  </span>
                );
              },
            }}
          >
            {STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {t(selectCustomerPriceQuoteStatusLabelKey(status))}
              </MenuItem>
            ))}
          </TextField>
        </Field>

        <Field label={label("VALID_UNTIL")}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={modal.dateLocale}
          >
            <DatePicker
              open={modal.isDateOpen}
              onOpen={() => modal.setIsDateOpen(true)}
              onClose={() => modal.setIsDateOpen(false)}
              value={
                modal.form.validUntil
                  ? new Date(`${modal.form.validUntil}T00:00:00`)
                  : null
              }
              onChange={modal.setValidUntil}
              inputFormat="dd/MM/yyyy"
              components={{ OpenPickerIcon: CalenderIcon }}
              PopperProps={{
                sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={styles.input}
                  onClick={() => modal.setIsDateOpen(true)}
                  inputProps={{
                    ...params.inputProps,
                    placeholder: t(`${PRICE_QUOTE_LABELS}.SELECT_DATE`),
                    readOnly: true,
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Field>
      </Box>
    </>
  );
};

export default PriceQuoteFields;
